<template>
  <div class="fair-survey-step fair-survey-step--wide">
    <div class="fair-survey-step__head">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <FairInputText
      v-model="searchQuery"
      class="brand-selection-step__search"
      placeholder="Найти бренд"
      left-icon="search"
      show-clear-button
    />

    <div class="brand-selection-step__grid">
      <button
        v-for="brand in filteredBrands"
        :key="brand.id"
        type="button"
        class="brand-selection-step__item"
        :class="{
          'brand-selection-step__item--selected': isBrandSelected(brand.id),
        }"
        :aria-pressed="isBrandSelected(brand.id)"
        @click="toggleBrand(brand.id)"
      >
        <FairCheckboxIndicator :checked="isBrandSelected(brand.id)" />
        <span class="brand-selection-step__name">{{ brand.name }}</span>
      </button>
    </div>

    <p
      v-if="!filteredBrands.length"
      class="fair-survey-step__note"
    >
      Ничего не найдено
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useGiftModal } from '@/features/gift/store'
import type { GiftStep } from '@/features/gift/types/gift-config'
import { FairCheckboxIndicator } from '@/ui/primitives/FairCheckbox'
import { FairInputText } from '@/ui/primitives/FairInputText'

interface Props {
  step: GiftStep & { type: 'brand_selection' }
}

const props = defineProps<Props>()
const giftStore = useGiftModal()

const searchQuery = ref('')

const selectedBrands = computed(() => {
  const data = giftStore.giftModal.giftData[props.step.id] || []
  // Миграция: если в store есть старые данные (названия), преобразуем их в ID
  if (data.length > 0 && typeof data[0] === 'string') {
    return data.map((name: string) => {
      const brand = props.step.brands.find((b) => b.name === name)
      return brand?.id
    }).filter((id: any) => id !== undefined)
  }
  return data
})

const filteredBrands = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.step.brands
  }
  const query = searchQuery.value.toLowerCase()
  return props.step.brands.filter((brand) =>
    brand.name.toLowerCase().includes(query)
  )
})

function isBrandSelected(brandId: number): boolean {
  return selectedBrands.value.includes(brandId)
}

function toggleBrand(brandId: number) {
  const currentBrands = [...selectedBrands.value]
  const index = currentBrands.indexOf(brandId)

  if (index > -1) {
    currentBrands.splice(index, 1)
  } else {
    currentBrands.push(brandId)
  }

  giftStore.updateGiftData(props.step.id, currentBrands)
}

onMounted(() => {
  // Initialize with existing data if any
  if (!giftStore.giftModal.giftData[props.step.id]) {
    giftStore.updateGiftData(props.step.id, [])
  }
})
</script>

<style scoped>
.brand-selection-step__search {
  max-width: 360px;
}

.brand-selection-step__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--space-8);
}

.brand-selection-step__item {
  display: flex;
  align-items: center;
  gap: var(--space-12);
  margin: 0;
  padding: var(--space-12);
  border: 1px solid var(--color-line-hairline);
  border-radius: var(--radius-card);
  background: var(--color-bg-surface);
  font-family: var(--fair-font-body, Inter, sans-serif);
  font-size: 14px;
  line-height: 1.2;
  text-align: left;
  color: var(--color-text-primary);
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.brand-selection-step__item:hover {
  border-color: var(--color-border-control);
}

.brand-selection-step__item--selected {
  border-color: var(--color-primary);
  background: var(--color-fill-chip);
}

.brand-selection-step__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (prefers-reduced-motion: reduce) {
  .brand-selection-step__item {
    transition: none;
  }
}
</style>
